import apiService from 'service/apiService';
import { buildEndpointUrl } from 'config/apiConfig';

// Cache simple en memoria para evitar llamadas redundantes durante la sesión
const memoryCache = {
  suggestions: new Map(), // key: query -> value: array de sugerencias normalizadas
  details: new Map(), // key: placeId -> value: detalles normalizados
};

const normalizeSuggestion = (item) => {
  // Backend puede devolver distintas formas; aseguramos { placeId, label }
  return {
    placeId: item.place_id || item.placeId || item.id || item.value || '',
    label: item.description || item.label || item.name || item.text || item.address || '',
  };
};

const normalizeDetails = (data) => {
  const ensureString = (val) => {
    if (val == null) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'number') return String(val);
    if (typeof val === 'object') {
      if (typeof val.name === 'string') return val.name;
      if (typeof val.long_name === 'string') return val.long_name;
      if (typeof val.short_name === 'string') return val.short_name;
    }
    return '';
  };

  const pickFirst = (obj, keys) => {
    for (const key of keys) {
      if (obj && Object.prototype.hasOwnProperty.call(obj, key)) {
        const v = obj[key];
        const str = ensureString(v);
        if (str) return str;
      }
    }
    return '';
  };

  // Soporte para respuestas tipo Google: components: [{ types: ['locality'], long_name: '...' }]
  const fromComponents = (components) => {
    if (!Array.isArray(components)) return {};
    const findType = (type) => {
      const comp = components.find((c) => Array.isArray(c.types) && c.types.includes(type));
      return comp ? (comp.long_name || comp.short_name || '') : '';
    };
    return {
      city: findType('locality') || findType('postal_town') || findType('sublocality'),
      state: findType('administrative_area_level_1') || findType('administrative_area_level_2'),
      country: findType('country'),
      zipCode: findType('postal_code'),
    };
  };

  const root = data || {};
  const address = root.address || root.result || root.details || root.data || root;

  // Lectura directa desde address_components (según contrato compartido)
  const addressComponents = address.address_components || root.address_components || null;
  if (addressComponents && typeof addressComponents === 'object') {
    const city = ensureString(addressComponents.city);
    const state = ensureString(addressComponents.state);
    const country = ensureString(addressComponents.country);
    const zipCode = ensureString(addressComponents.zipCode || addressComponents.zip_code);
    return { city: city || '', state: state || '', country: country || '', zipCode: zipCode || '' };
  }

  // Intento directo por llaves conocidas
  let city = pickFirst(address, ['city', 'city_name', 'locality', 'town', 'municipality']);
  let state = pickFirst(address, ['state', 'state_name', 'region', 'administrative_area_level_1', 'state_code']);
  let country = pickFirst(address, ['country', 'country_name', 'countryCode', 'country_code']);
  let zipCode = pickFirst(address, ['zipCode', 'zip_code', 'postal_code', 'postcode']);

  // Intento por address anidado si no se obtuvo
  if (!(city && state && country && zipCode)) {
    const nestedAddr = address.address || {};
    city = city || pickFirst(nestedAddr, ['city', 'city_name', 'locality']);
    state = state || pickFirst(nestedAddr, ['state', 'state_name', 'administrative_area_level_1']);
    country = country || pickFirst(nestedAddr, ['country', 'country_name', 'country_code']);
    zipCode = zipCode || pickFirst(nestedAddr, ['zipCode', 'zip_code', 'postal_code']);
  }

  // Intento por components (estilo Google)
  if (!(city && state && country && zipCode)) {
    const compValues = fromComponents(address.components || root.components || []);
    city = city || compValues.city;
    state = state || compValues.state;
    country = country || compValues.country;
    zipCode = zipCode || compValues.zipCode;
  }

  return { city: city || '', state: state || '', country: country || '', zipCode: zipCode || '' };
};

const coerceToJson = (response) => {
  if (typeof response === 'string') {
    try {
      return JSON.parse(response);
    } catch (_) {
      return {};
    }
  }
  return response || {};
};

const pickFirstArray = (obj) => {
  if (Array.isArray(obj)) return obj;
  if (!obj || typeof obj !== 'object') return [];
  const candidates = [
    'predictions',
    'suggestions',
    'items',
    'content',
    'results',
    'addresses',
    'data',
  ];
  for (const key of candidates) {
    const val = obj[key];
    if (Array.isArray(val)) return val;
  }
  // nested data.suggestions or data.results
  const data = obj.data;
  if (data && typeof data === 'object') {
    for (const key of ['suggestions', 'results', 'items', 'content']) {
      const val = data[key];
      if (Array.isArray(val)) return val;
    }
  }
  return [];
};

export const fetchAddressSuggestions = async (query, { signal } = {}) => {
  const trimmed = (query || '').trim();
  if (trimmed.length < 2) return [];

  if (memoryCache.suggestions.has(trimmed)) {
    return memoryCache.suggestions.get(trimmed);
  }

  const endpoint = buildEndpointUrl('PARAMETERS', `/address/autocomplete?input=${encodeURIComponent(trimmed)}`);
  const response = await apiService.request(endpoint, { method: 'GET', signal });
  const json = coerceToJson(response);
  const list = Array.isArray(json) ? json : pickFirstArray(json);
  const normalized = list.map(normalizeSuggestion).filter(s => s.placeId && s.label);
  memoryCache.suggestions.set(trimmed, normalized);
  return normalized;
};

export const fetchAddressDetails = async (placeId, { signal } = {}) => {
  const key = String(placeId || '').trim();
  if (!key) return { city: '', state: '', country: '', zipCode: '' };

  if (memoryCache.details.has(key)) {
    return memoryCache.details.get(key);
  }

  const endpoint = buildEndpointUrl('PARAMETERS', `/address/details/${encodeURIComponent(key)}`);
  const response = await apiService.request(endpoint, { method: 'GET', signal });
  const json = coerceToJson(response);
  const data = (json && typeof json === 'object' && (json.details || json.result || json.data)) || json || {};
  const normalized = normalizeDetails(data);
  memoryCache.details.set(key, normalized);
  return normalized;
};

export default {
  fetchAddressSuggestions,
  fetchAddressDetails,
};


